import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { auth } from '../firebase'; // Adjust path as needed
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';

const API_URL = 'https://ufb-1.onrender.com/api/inventory';

const UniteeHybrid = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  
  const user = auth.currentUser;

  const fetchUserItems = async () => {
    if (!user) {
      setError("User not logged in");
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/user/${user.uid}`);
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to fetch user inventory');
      }
      const data = await res.json();
      setItems(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserItems();
  }, [user]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      const res = await fetch(`${API_URL}/${id}`, { method: 'DELETE' });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Delete failed');
      }
      const result = await res.json();
      if (result.success) {
        setItems(items.filter(item => item.id !== id));
      } else {
        throw new Error('Delete failed');
      }
    } catch (err) {
      alert(err.message);
    }
  };

  const filteredItems = items.filter(item =>
    item.productName.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="flex justify-center items-center h-64"><div className="text-xl">Loading your inventory...</div></div>;
  if (error) return <div className="text-red-500 text-center">{error}</div>;

  return (

   <div className="flex h-screen bg-gray-50">
  {/* Sidebar */}
  <Sidebar />

  {/* Main content */}
  <div className="flex-1 flex flex-col min-w-0 overflow-hidden md:ml-64">
    <Header />
    <div className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
        <div>
          <h2 className="text-3xl font-extrabold text-blue-900 tracking-tight">Hybrid Inventory</h2>
          {/* <p className="text-gray-600 mt-1">Your Inventory Our Printing </p> */}
        </div>
        <input
          type="text"
          placeholder="Search by product name..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full md:w-72 border-2 border-blue-200 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white shadow-sm"
        />
      </div>
      
      {filteredItems.length === 0 && !loading && (
        <div className="text-center py-12">
          <div className="text-gray-500 text-xl mb-4">
            {search ? "No products found matching your search." : "No inventory items found."}
          </div>
          {!search && (
            <p className="text-gray-400">Start by creating your first inventory item.</p>
          )}
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredItems.map(item => {
          const totalStock = Object.values(item.stockBySize || {}).reduce((sum, stock) => sum + stock, 0);
          const sizes = Object.keys(item.stockBySize || {});
          
          // Check for low stock items
          const lowStockSizes = sizes.filter(size => {
            const currentStock = item.stockBySize[size] || 0;
            const threshold = item.stockThresholds?.[size] || 0;
            return threshold > 0 && currentStock <= threshold;
          });
          
          const hasLowStock = lowStockSizes.length > 0;
          
          return (
            <div key={item.id} className={`bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow border overflow-hidden ${hasLowStock ? 'border-red-200' : 'border-gray-100'}`}>
              <div className="h-64 bg-gradient-to-br from-white to-white flex items-center justify-center relative">
                {hasLowStock && (
                  <div className="absolute top-2 right-2 bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold z-10">
                    ⚠️ Low Stock
                  </div>
                )}
                {item.imageUrl ? (
                  <img 
                    src={item.imageUrl} 
                    alt={item.productName}
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className={`flex items-center justify-center text-white ${item.imageUrl ? 'hidden' : 'flex'}`}>
                  <svg className="w-16 h-16" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{item.productName}</h3>
                <p className="text-gray-600 mb-3">{item.gsm}</p>
                
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">Available Sizes:</span>
                    <span className={`text-lg font-bold ${hasLowStock ? 'text-red-600' : 'text-green-600'}`}>
                      {totalStock} total
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {sizes.map(size => {
                      const currentStock = item.stockBySize[size] || 0;
                      const threshold = item.stockThresholds?.[size] || 0;
                      const isLowStock = threshold > 0 && currentStock <= threshold;
                      
                      return (
                        <span key={size} className={`px-2 py-1 text-xs rounded-full ${isLowStock ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`}>
                          {size}: {currentStock}
                          {threshold > 0 && (
                            <span className="text-xs opacity-75"> (≤{threshold})</span>
                          )}
                        </span>
                      );
                    })}
                  </div>
                  {hasLowStock && (
                    <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-xs text-red-700 font-medium">
                        Low stock alert for: {lowStockSizes.join(', ')}
                      </p>
                    </div>
                  )}
                </div>
                
                {/* <div className="flex gap-2">
                  <Link 
                    to={`/inventory/details/${item.id}`} 
                    className="flex-1 bg-blue-600 text-white text-center p-2 rounded-lg hover:bg-blue-700 font-semibold"
                  >
                    View
                  </Link>
                  <Link 
                    to={`/inventory/edit/${item.id}`} 
                    className="px-3 py-2 bg-yellow-400 text-yellow-900 rounded-lg hover:bg-yellow-500 font-semibold"
                  >
                    Edit
                  </Link>
                  <button 
                    onClick={() => handleDelete(item.id)} 
                    className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 font-semibold"
                  >
                    Delete
                  </button>
                </div> */}
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Add New Item Button */}
      <div className="fixed bottom-6 right-6">
        <Link
          to="/inventory/create-user-item"
          className="bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </Link>
      </div>
    </div>
    </div>
    </div>
  );
};

export default UniteeHybrid;